/**
 * MODULE: domain/pages/frontend/components/editor/PagesCrmContactPreviewBlockView.tsx
 * PURPOSE: Live CRM contact snapshot card block (Sprint 9)
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineUser } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import { useCrmEntityPreview } from '../../hooks/useCrmEntityPreview';
import { crmMentionPath } from '../../utils/crmMentionUtils';
import type { PageContactPreview } from '../../../contracts/pages.types';
import { CrmPreviewBlockShell } from './CrmPreviewBlockShell';

export function PagesCrmContactPreviewBlockView({ node, selected }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const contactId = String(node.attrs.contactId ?? '');
  const fallbackLabel = String(node.attrs.contactLabel ?? 'Untitled contact').trim() || 'Untitled contact';
  const { data: rawData, isLoading, isError, refetch, isFetching } = useCrmEntityPreview(
    'contact',
    contactId || null,
    { enabled: !isPublicView }
  );
  const data = rawData as PageContactPreview | undefined;

  const metaParts = isPublicView
    ? []
    : [data?.job_title, data?.company_name, data?.email, data?.phone].filter(Boolean);
  const metaLine = isPublicView
    ? 'Saved contact reference from the shared page'
    : metaParts.join(' · ') || 'Open in CRM for full details';

  return (
    <NodeViewWrapper className="my-3">
      <CrmPreviewBlockShell
        testIdPrefix="pages-crm-contact-preview"
        selected={selected}
        ringClass="ring-blue-400"
        borderClass="border-blue-200 dark:border-blue-900/50"
        gradientClass="from-blue-50 to-white dark:from-blue-950/30 dark:to-gray-900"
        icon={
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">
            <HiOutlineUser className="h-5 w-5" aria-hidden />
          </span>
        }
        badgeLabel="CRM contact"
        badgeClassName="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
        title={isPublicView ? fallbackLabel : (data?.name ?? fallbackLabel)}
        meta={metaLine}
        isLoading={!isPublicView && isLoading}
        isError={!isPublicView && isError}
        errorHint="Could not load live contact data. Showing saved label."
        isFetching={!isPublicView && isFetching}
        entityId={contactId}
        onRefresh={() => void refetch()}
        onOpenCrm={() => contactId && navigate(crmMentionPath('contact', contactId))}
        publicReadOnly={isPublicView}
      />
    </NodeViewWrapper>
  );
}
